import { loadFragment } from 'blocks/fragment/fragment.ts';
import {
	buildBlock,
	decorateBlock,
	decorateIcons,
	loadBlock,
} from 'scripts/aem.js';
import './modal.css';
import './modal.rebrand.css';
import './objective-glossary.css';
import './modal-flyout.css';
import { getOrigin } from 'utils/window-utils.ts';

// This is not a traditional block, so there is no decorate function. Instead, links to
// a */modals/* path  are automatically transformed into a modal. Other blocks can also use
// the createModal() and openModal() functions.
/**
 * Create a modal
 * @param {Node[]} contentNodes Array of Nodes to append to dialog content
 * @param {string} className Optional className to append to or remove from HTML body
 * @param {boolean} appendCloseToContent Optional flag to append close button to dialog content instead of dialog
 * @param {boolean} removeBlockOnClose Optional flag to opt out of removing a block on close, so it can be reused
 * @param {boolean} isFlyout Optional flag to create a flyout modal
 * @param {function} onClose Optional callback function to run logic on close of modal
 */
type CreateModalProps = {
	contentNodes: Node[];
	className?: string;
	appendCloseToContent?: boolean;
	removeBlockOnClose?: boolean;
	autofocusCloseBtn?: boolean;
	hideCloseButton?: boolean;
	closeDialogOnOutsideClick?: boolean;
	isFlyout?: boolean;
	onClose?: () => void;
};
export async function createModal({
	contentNodes,
	className,
	appendCloseToContent = false,
	removeBlockOnClose = true,
	autofocusCloseBtn = true,
	hideCloseButton = false,
	closeDialogOnOutsideClick = true,
	isFlyout = false,
	onClose,
}: CreateModalProps) {
	const dialog = document.createElement('dialog');
	if (isFlyout) dialog.classList.add('flyout');
	const dialogContent = document.createElement('div');
	dialogContent.classList.add('modal-content');
	dialogContent.append(...contentNodes);
	dialog.append(dialogContent);

	const closeDialog = () => {
		if (isFlyout) {
			dialog.classList.add('closing');
			setTimeout(() => {
				dialog.close();
			}, 400);
		} else {
			dialog.close();
		}
	};

	if (!hideCloseButton) {
		const closeButton = document.createElement('button');
		closeButton.classList.add('close-button');
		closeButton.setAttribute('aria-label', 'Close');
		if (autofocusCloseBtn) closeButton.autofocus = true;
		closeButton.type = 'button';
		closeButton.innerHTML = '<span class="icon icon-close"></span>';
		closeButton.addEventListener('click', () => closeDialog());
		appendCloseToContent
			? dialogContent.append(closeButton)
			: dialog.append(closeButton);
		decorateIcons(closeButton);
	}

	// close dialog on clicks outside the dialog. https://stackoverflow.com/a/70593278/79461
	dialog.addEventListener('click', (event) => {
		if (
			!dialogContent.contains(event.target as Node) &&
			closeDialogOnOutsideClick
		) {
			closeDialog();
		}
	});

	const block = buildBlock('modal', '');
	document.querySelector('main')?.after(block);
	decorateBlock(block);
	await loadBlock(block);

	dialog.addEventListener('close', () => {
		document.body.classList.remove('modal-open');
		if (className) document.body.classList.remove(className);
		if (removeBlockOnClose) block?.remove();
		if (onClose) onClose();
	});

	block.append(dialog);
	return {
		block,
		dialog,
		showModal: (contentNodes?: Node[]) => {
			if (contentNodes) {
				dialogContent.innerHTML = '';
				dialogContent.append(...contentNodes);
			}
			dialog.showModal();
			// Google Chrome restores the scroll position when the dialog is reopened,
			// so we need to reset it.
			setTimeout(() => {
				dialogContent.scrollTop = 0;
			}, 0);

			document.body.classList.add('modal-open');
			if (className) document.body.classList.add(className);
		},
	};
}

export async function openModal(fragmentUrl: string) {
	const path = fragmentUrl.startsWith('http')
		? new URL(fragmentUrl, getOrigin()).pathname
		: fragmentUrl;

	const fragment = await loadFragment(path);

	const { showModal } = await createModal({
		contentNodes: Array.from(fragment?.childNodes ?? []),
	});
	showModal();
}
